import { ReactNode } from 'react';

import { store } from '../store';
import { addSnackbarMessage, removeSnackbarMessage } from '../store/actions/snackbar';
import { IIntegrationError, NotificationListEnum } from '../types/shell';

type TypeOptions = 'error' | 'info' | 'success' | 'warning' | 'promise' | 'default';

type ToastId = string | number;

interface ICustomToastOptions {
  shouldClear?: boolean;
}

interface IToastOptions {
  onOpen?: () => void;
  onClose?: () => void;
  dataAction?: string;
  hideIcon?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface IPromiseParams<DataType> {
  pending: string | { render: () => string };
  success?: string | { render: (data: { data: DataType }) => string };
  error?: string | { render: (error: { data: { message: string } }) => string };
}

interface IToastFunctionTypeProps extends ICustomToastOptions {
  content: ToastId | ReactNode;
  options?: IToastOptions;
  dataAction?: string;
}

interface IShowPromiseFunctionProps<DataType> extends ICustomToastOptions {
  promise: Promise<DataType> | (() => Promise<DataType>);
  promiseParams: IPromiseParams<DataType>;
  options?: IToastOptions;
}

const typeMapping: Record<TypeOptions, NotificationListEnum> = {
  error: NotificationListEnum.Error,
  info: NotificationListEnum.Info,
  success: NotificationListEnum.Success,
  warning: NotificationListEnum.Warning,
  promise: NotificationListEnum.Info,
  default: NotificationListEnum.Info,
};

const addSnackbarMessageToStore = (
  message: string | ReactNode,
  type: NotificationListEnum,
  onClose?: () => void,
  dataAction?: string,
  action?: { label: string; onClick: () => void },
  hideIcon?: boolean,
): ToastId => {
  const key = new Date().getTime();
  store.dispatch(
    addSnackbarMessage({
      message,
      type,
      key,
      onClose,
      dataAction,
      action,
      hideIcon,
    }),
  );
  return key.toString();
};

const removeLastSnackbarMessage = (): void => {
  store.dispatch(removeSnackbarMessage());
};

const promiseSnackbar = <DataType>({
  promise,
  promiseParams: { pending, success, error },
}: IShowPromiseFunctionProps<DataType>): ToastId => {
  const pendingMessage = typeof pending === 'string' ? pending : pending?.render() || '';

  const pendingKey = addSnackbarMessageToStore(pendingMessage, NotificationListEnum.Loading);

  const p = typeof promise === 'function' ? promise() : promise;

  p.then((result) => {
    removeLastSnackbarMessage();
    if (success) {
      const successMessage = typeof success === 'string' ? success : success?.render({ data: result }) || '';
      addSnackbarMessageToStore(successMessage, NotificationListEnum.Success);
    }
    return result;
  }).catch((err) => {
    removeLastSnackbarMessage();
    if (err) {
      const errorMessage = typeof error === 'string' ? error : error?.render({ data: err }) || '';
      addSnackbarMessageToStore(errorMessage, NotificationListEnum.Error);
    }
  });

  return pendingKey;
};

const createToast = <DataType = {}, T extends TypeOptions = TypeOptions>(
  type: T,
  options: T extends 'promise' ? IShowPromiseFunctionProps<DataType> : IToastFunctionTypeProps,
): ToastId => {
  if (type === 'promise') {
    return promiseSnackbar(options as IShowPromiseFunctionProps<DataType>);
  }

  const { content, options: toastOptions } = options as IToastFunctionTypeProps;
  const message = content;
  const snackbarType = typeMapping[type];

  return addSnackbarMessageToStore(
    message,
    snackbarType,
    toastOptions?.onClose,
    (options as IToastFunctionTypeProps)?.dataAction,
    toastOptions?.action,
    toastOptions?.hideIcon,
  );
};

const showToast = <DataType = {}, T extends TypeOptions = TypeOptions>(
  _: ToastId | undefined,
  type: T,
  config: T extends 'promise' ? IShowPromiseFunctionProps<DataType> : IToastFunctionTypeProps,
): Promise<ToastId> => {
  const id = createToast<DataType, T>(type, config);
  config.options?.onOpen?.();
  return Promise.resolve(id);
};

const notification = {
  show: showToast,
  error: <DataType>(initialId: ToastId | undefined, options: IToastFunctionTypeProps) => {
    return showToast<DataType>(initialId, 'error', options);
  },
  info: <DataType>(initialId: ToastId | undefined, options: IToastFunctionTypeProps) => {
    return showToast<DataType>(initialId, 'info', options);
  },
  success: <DataType>(initialId: ToastId | undefined, options: IToastFunctionTypeProps) => {
    return showToast<DataType>(initialId, 'success', options);
  },
  warning: <DataType>(initialId: ToastId | undefined, options: IToastFunctionTypeProps) => {
    return showToast<DataType>(initialId, 'warning', options);
  },
  promise: <DataType>(initialId: ToastId | undefined, options: IShowPromiseFunctionProps<DataType>) => {
    return showToast<DataType>(initialId, 'promise', options);
  },
  comingSoon: <DataType>() => {
    return notification.info<DataType>(NotificationListEnum.ComingSoon, { content: 'Coming soon!' });
  },
  accessDenied: <DataType>(content?: string) => {
    return notification.error<DataType>(NotificationListEnum.AccessDenied, {
      content: content || 'Access denied!',
      shouldClear: false,
    });
  },
  outOfMoney: <DataType>(content: string | ReactNode) => {
    return notification.warning<DataType>(NotificationListEnum.Warning, { content });
  },
  noAsset: <DataType>(asset: string) => {
    return notification.error<DataType>(NotificationListEnum.Error, { content: `No ${asset} with that id!` });
  },
  enterValidData: <DataType>(asset: string = 'data') => {
    return notification.error<DataType>(NotificationListEnum.EnterValidData, {
      content: `Please, enter a valid ${asset}`,
    });
  },
  integrationSuccess: <DataType>() => {
    return notification.success<DataType>(NotificationListEnum.AddIntegrationSuccess, {
      content: `The integration was added successfully!`,
    });
  },
  integrationError: <DataType>(content: IIntegrationError) => {
    const { error } = content;
    return notification.error<DataType>(NotificationListEnum.AddIntegrationError, {
      content: `${error}`,
    });
  },
};

export default notification;
